import { backspaceKey, optionKey, shiftKey } from '@/utilities/constants/specialKeyboardCharacters';

export const globalCreationShortcuts = {
    tasks: {
        displayShortcut: `${shiftKey} ${optionKey} T`,
        hotkey: 'shift+alt+t',
    },
} as const;

export type NavigationRouteId =
    | 'home'
    | 'inbox'
    | 'folders'
    | 'drafts'
    | 'archive'
    | 'search'
    | 'investments'
    | 'memos'
    | 'pipelines'
    | 'dashboards'
    | 'lists'
    | 'tasks'
    | 'teams'
    | 'settings'
    | 'trash';

export type NavigationRouteShortcut = {
    id: NavigationRouteId;
    label: string;
    path: string;
    siderKey: string;
    exactRoute: string;
    keyboardShortcut: string;
    displayShortcut: string;
    hotkey: string;
    activeRoutePrefixes: string[];
    activeRouteExclusions?: string[];
};

const shiftedDisplayShortcut = (key: string) => `${shiftKey} ${key}`;

export const navigationRouteShortcuts: Record<NavigationRouteId, NavigationRouteShortcut> = {
    home: {
        id: 'home',
        label: 'Home',
        path: '/',
        siderKey: '',
        exactRoute: '/',
        keyboardShortcut: 'Enter',
        displayShortcut: shiftedDisplayShortcut('Enter'),
        hotkey: 'shift+enter',
        activeRoutePrefixes: ['/'],
    },
    inbox: {
        id: 'inbox',
        label: 'Inbox',
        path: '/inbox',
        siderKey: 'inbox',
        exactRoute: '/inbox',
        keyboardShortcut: 'Z',
        displayShortcut: shiftedDisplayShortcut('Z'),
        hotkey: 'shift+z',
        activeRoutePrefixes: ['/inbox'],
    },
    folders: {
        id: 'folders',
        label: 'Folders',
        path: '/folders',
        siderKey: 'folders',
        exactRoute: '/folders',
        keyboardShortcut: 'F',
        displayShortcut: shiftedDisplayShortcut('F'),
        hotkey: 'shift+f',
        activeRoutePrefixes: ['/folders'],
    },
    drafts: {
        id: 'drafts',
        label: 'Drafts',
        path: '/drafts',
        siderKey: 'drafts',
        exactRoute: '/drafts',
        keyboardShortcut: 'Q',
        displayShortcut: shiftedDisplayShortcut('Q'),
        hotkey: 'shift+q',
        activeRoutePrefixes: ['/drafts'],
    },
    archive: {
        id: 'archive',
        label: 'Archive',
        path: '/archive',
        siderKey: 'archive',
        exactRoute: '/archive',
        keyboardShortcut: 'A',
        displayShortcut: shiftedDisplayShortcut('A'),
        hotkey: 'shift+a',
        activeRoutePrefixes: ['/archive'],
    },
    search: {
        id: 'search',
        label: 'Search',
        path: '/search',
        siderKey: 'search',
        exactRoute: '/search',
        keyboardShortcut: 'S',
        displayShortcut: shiftedDisplayShortcut('S'),
        hotkey: 'shift+s',
        activeRoutePrefixes: ['/search'],
    },
    investments: {
        id: 'investments',
        label: 'Investments',
        path: '/investments',
        siderKey: 'investments',
        exactRoute: '/investments',
        keyboardShortcut: 'I',
        displayShortcut: shiftedDisplayShortcut('I'),
        hotkey: 'shift+i',
        activeRoutePrefixes: ['/investments'],
    },
    memos: {
        id: 'memos',
        label: 'Memos',
        path: '/memos',
        siderKey: 'memos',
        exactRoute: '/memos',
        keyboardShortcut: 'M',
        displayShortcut: shiftedDisplayShortcut('M'),
        hotkey: 'shift+m',
        activeRoutePrefixes: ['/memos'],
    },
    pipelines: {
        id: 'pipelines',
        label: 'Pipelines',
        path: '/pipelines',
        siderKey: 'pipelines',
        exactRoute: '/pipelines',
        keyboardShortcut: 'P',
        displayShortcut: shiftedDisplayShortcut('P'),
        hotkey: 'shift+p',
        activeRoutePrefixes: ['/pipelines'],
    },
    dashboards: {
        id: 'dashboards',
        label: 'Dashboards',
        path: '/relations/dashboards',
        siderKey: 'relations/dashboards',
        exactRoute: '/relations/dashboards',
        keyboardShortcut: 'D',
        displayShortcut: shiftedDisplayShortcut('D'),
        hotkey: 'shift+d',
        activeRoutePrefixes: ['/relations/dashboards', '/dashboards'],
    },
    lists: {
        id: 'lists',
        label: 'Lists',
        path: '/relations/lists',
        siderKey: 'relations/lists',
        exactRoute: '/relations/lists',
        keyboardShortcut: 'L',
        displayShortcut: shiftedDisplayShortcut('L'),
        hotkey: 'shift+l',
        activeRoutePrefixes: ['/relations/lists', '/lists'],
    },
    tasks: {
        id: 'tasks',
        label: 'Tasks',
        path: '/relations/tasks',
        siderKey: 'relations/tasks',
        exactRoute: '/relations/tasks',
        keyboardShortcut: 'K',
        displayShortcut: shiftedDisplayShortcut('K'),
        hotkey: 'shift+k',
        activeRoutePrefixes: ['/relations/tasks'],
    },
    teams: {
        id: 'teams',
        label: 'Teams',
        path: '/settings/workspace/permissions/teams',
        siderKey: 'teams',
        exactRoute: '/settings/workspace/permissions/teams',
        keyboardShortcut: 'T',
        displayShortcut: shiftedDisplayShortcut('T'),
        hotkey: 'shift+t',
        activeRoutePrefixes: ['/settings/workspace/permissions/teams'],
    },
    settings: {
        id: 'settings',
        label: 'Settings',
        path: '/settings/account/general/overview',
        siderKey: 'settings',
        exactRoute: '/settings/account/general/overview',
        keyboardShortcut: 'X',
        displayShortcut: shiftedDisplayShortcut('X'),
        hotkey: 'shift+x',
        activeRoutePrefixes: ['/settings'],
        activeRouteExclusions: ['/settings/workspace/permissions/teams'],
    },
    trash: {
        id: 'trash',
        label: 'Trash',
        path: '/trash',
        siderKey: 'trash',
        exactRoute: '/trash',
        keyboardShortcut: backspaceKey,
        displayShortcut: shiftedDisplayShortcut(backspaceKey),
        hotkey: 'shift+backspace',
        activeRoutePrefixes: ['/trash'],
    },
};

export const navigationRouteShortcutList = Object.values(navigationRouteShortcuts);

const pathMatchesPrefix = (pathname: string, prefix: string) => {
    if (prefix === '/') {
        return pathname === '/';
    }

    return pathname === prefix || pathname.startsWith(`${prefix}/`);
};

export const isNavigationRouteActive = (routeId: NavigationRouteId | undefined, pathname: string) => {
    if (!routeId) {
        return false;
    }

    const route = navigationRouteShortcuts[routeId];

    if (!route) {
        return false;
    }

    if (route.activeRouteExclusions?.some((prefix) => pathMatchesPrefix(pathname, prefix))) {
        return false;
    }

    return route.activeRoutePrefixes.some((prefix) => pathMatchesPrefix(pathname, prefix));
};
